import { WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { Bound } from '@blocksuite/global/gfx';
import type { TestAffineEditorContainer } from '@blocksuite/integration-test';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('custom-outline-panel')
export class CustomOutlinePanel extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .custom-outline-container {
      position: fixed;
      top: 0;
      right: 0;
      border: 1px solid var(--affine-border-color, #e3e2e4);
      background: var(--affine-background-overlay-panel-color, #ffffff);
      height: 100vh;
      width: 320px;
      box-sizing: border-box;
      z-index: 1000;
      box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
      display: flex;
      flex-direction: column;
      color: var(--affine-text-primary-color, #000000);
      font-family: sans-serif;
    }
    .layers-header {
      padding: 16px;
      font-weight: 600;
      font-size: 16px;
      border-bottom: 1px solid var(--affine-border-color, #e3e2e4);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .layers-close {
      cursor: pointer;
      font-size: 18px;
      opacity: 0.6;
    }
    .layers-close:hover {
      opacity: 1;
    }
    .layers-list {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
    }
    .layer-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      margin-bottom: 4px;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.2s;
      border: 1px solid transparent;
    }
    .layer-item:hover {
      background: var(--affine-hover-color, #f4f4f5);
    }
    .layer-item.selected {
      background: var(--affine-hover-color, #f4f4f5);
      border-color: #1E96EB;
    }
    .layer-info {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
    }
    .layer-name {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 180px;
    }
    .layer-actions {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .layer-btn {
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      opacity: 0.6;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--affine-text-secondary-color);
    }
    .layer-btn:hover {
      opacity: 1;
      background: var(--affine-active-color, #e4e4e7);
    }
    .layer-btn.delete:hover {
      color: #ef4444;
      background: #fee2e2;
    }
  `;

  private _selectElement(el: any) {
    const gfx = this.editor.host?.std.get(GfxControllerIdentifier);
    if (!gfx) return;
    gfx.selection.set({
      elements: [el.id],
      editing: false,
    });
    if (this._zoomOnClick && el.xywh) {
      const bound = Bound.deserialize(el.xywh);
      gfx.viewport.setViewportByBound(bound);
    }
  }

  private _deleteElement(el: any, event: MouseEvent) {
    event.stopPropagation();
    const gfx = this.editor.host?.std.get(GfxControllerIdentifier);
    if (!gfx) return;
    gfx.deleteElement(el);
    this.requestUpdate();
  }

  private _getElementName(el: any): string {
    if (el.type) return `Shape: ${el.type}`;
    if (el.flavour) {
      const name = el.flavour.split(':')[1] || el.flavour;
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    return el.constructor.name || 'Element';
  }

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.add(
      this.editor.doc.history.onUpdated.subscribe(() => {
        this.requestUpdate();
      })
    );
  }

  override render() {
    if (!this._show) return nothing;

    const gfx = this.editor.host?.std.get(GfxControllerIdentifier);
    const elements = gfx ? (gfx.gfxElements || []) : [];
    const selectedIds = gfx ? (gfx.selection.selectedElements.map(e => e.id) || []) : [];

    return html`
      <div class="custom-outline-container">
        <div class="layers-header">
          <span>Elements (${elements.length})</span>
          <div style="display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: normal; margin-left: auto; margin-right: 12px;">
            <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
              <input type="checkbox" ?checked=${this._zoomOnClick} @change=${(e: Event) => this._zoomOnClick = (e.target as HTMLInputElement).checked}>
              Zoom
            </label>
          </div>
          <span class="layers-close" @click=${() => this._show = false}>✕</span>
        </div>
        <div class="layers-list">
          ${elements.map(el => {
            const isSelected = selectedIds.includes(el.id);
            return html`
              <div 
                class="layer-item ${isSelected ? 'selected' : ''}" 
                @click=${() => this._selectElement(el)}
              >
                <div class="layer-info">
                  <span class="layer-name">${this._getElementName(el)}</span>
                </div>
                <div class="layer-actions">
                  <button class="layer-btn delete" @click=${(e: MouseEvent) => this._deleteElement(el, e)}>✕</button>
                </div>
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  toggleDisplay() {
    this._show = !this._show;
  }

  @state()
  private accessor _show = false;

  @state()
  private accessor _zoomOnClick = false;

  @property({ attribute: false })
  accessor editor!: TestAffineEditorContainer;
}

declare global {
  interface HTMLElementTagNameMap {
    'custom-outline-panel': CustomOutlinePanel;
  }
}
